import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createArbitraryValueChart, createPredefinedValuesChart } from '@charts';
import { signalHelpers } from '@core';
import { getAvsDateRange } from '@ui';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, Subject } from 'rxjs';

import { WidgetDeviceChart } from '../../../models';
import { DashboardStore, getChartKey } from '../../../stores';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HighchartsChartComponent,
    NgxSkeletonLoaderModule,
  ],
  selector: 'avs-device-chart',
  templateUrl: './device-chart.component.html',
})
export class DeviceChartComponent {
  config = input.required<WidgetDeviceChart>();

  protected chartRef = signal<Highcharts.Chart | null>(null);
  protected highchartOptions = computed(() => {
    const timeseries = this.chartState();
    const paused = this.pausedState();

    if (!timeseries) {
      return null;
    }

    return !timeseries.measurementUnit || !timeseries.measurementUnit.isStep
      ? createArbitraryValueChart({ chart: timeseries, noTitle: true, paused, showDots: false })
      : createPredefinedValuesChart({ chart: timeseries, noTitle: true, paused, showDots: false });
  });
  protected Highcharts: typeof Highcharts = Highcharts;
  protected isLoading = computed(() => !this.chartState() || !this.pausedState());

  private store = inject(DashboardStore);
  private chartPayload = computed(() => {
    const { start } = getAvsDateRange(this.config().dateRange!);

    return {
      fromDateTimeUtc: start!.toISOString(),
      point: this.config().datapointName,
      toDateTimeUtc: new Date().toISOString(),
    };
  });
  private chartKey = computed(() => getChartKey(this.config().deviceId, this.config().datapointName, this.config().dateRange));
  private pausedKey = computed(() => getChartKey(this.config().deviceId, 'paused', this.config().dateRange));
  private chartState = computed(() => this.store.chartData(this.chartKey())());
  private pausedState = computed(() => this.store.chartData(this.pausedKey())());
  private refresh$ = new Subject<void>();

  constructor() {
    signalHelpers.untrackedEffect([this.chartPayload], (payload) => {
      const dateRange = this.config().dateRange;

      this.store.loadChartData(this.config().deviceId, payload, dateRange);
      this.store.loadChartData(this.config().deviceId, { ...payload, point: 'paused' }, dateRange);
    });

    this.refresh$
      .pipe(
        debounceTime(150),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.chartRef()?.reflow());
  }

  refresh() {
    this.refresh$.next();
  }
}
